import { useRef, useState } from "react";
import { FileText, Loader2, Trash2, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import type { Workshop } from "@/content/workshops";
import {
  DOC_BY_WORKSHOP,
  type DocContent,
  type DocSection,
} from "@/features/workshop/modals/doc-content";
import { resolveField } from "../workshop-override-content";
import {
  clearEffectiveOverrideField,
  setEffectiveOverride,
  useEffectiveWorkshopOverride,
} from "@/stores/useEffectiveWorkshopOverride";
import {
  clearPreReading,
  uploadPreReading,
} from "@/stores/useWorkshopOverrides";
import { isWorkshopBffEnabled } from "@/env";
import { DocsApiError } from "@/lib/docs-api";
import {
  AddButton,
  Field,
  ListCard,
  SectionFrame,
  Select,
  TextArea,
  TextInput,
} from "../editor-kit";
import { moveItem, sameJson } from "../editor-utils";

/**
 * Editor-local section shape: `body` (string | string[]) and `items` are
 * flattened to textareas (blank-line-separated paragraphs / one item per
 * line) and re-assembled on save.
 */
type EditableSection = {
  heading: string;
  kind: "section" | "callout";
  bodyText: string;
  intro: string;
  itemsText: string;
};

function toEditable(s: DocSection): EditableSection {
  const body = s.body === undefined ? "" : Array.isArray(s.body) ? s.body.join("\n\n") : s.body;
  return {
    heading: s.heading,
    kind: s.kind === "callout" ? "callout" : "section",
    bodyText: body,
    intro: s.intro ?? "",
    itemsText: (s.items ?? []).join("\n"),
  };
}

function fromEditable(s: EditableSection): DocSection {
  const out: DocSection = { heading: s.heading };
  if (s.kind === "callout") out.kind = "callout";
  const paras = s.bodyText.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  if (paras.length > 1) out.body = paras;
  else if (paras.length === 1) out.body = paras[0];
  const intro = s.intro.trim();
  if (intro) out.intro = intro;
  const items = s.itemsText.split("\n").map((i) => i.trim()).filter(Boolean);
  if (items.length > 0) out.items = items;
  return out;
}

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let n = bytes / 1024;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

/**
 * Upload affordance — sits above the authored-sections editor. An uploaded
 * document is shared (BFF blob) and TAKES PRECEDENCE: when one is set the
 * Pre-reading modal shows it instead of the sections below.
 */
function UploadBlock({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const upload = override?.docUpload;
  const bffEnabled = isWorkshopBffEnabled();
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setBusy(true);
    try {
      const ok = await uploadPreReading(wsId, file);
      if (ok) toast.success(`Uploaded ${file.name}`);
      else toast.error("Document upload needs the document backend (BFF).");
    } catch (err) {
      toast.error(err instanceof DocsApiError ? err.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-[rgba(var(--accent-true-blue-rgb),0.15)] bg-[rgba(var(--accent-true-blue-rgb),0.03)] p-3">
      <div className="font-mono text-fs-label tracking-[0.2em] text-[color:var(--text-muted)] uppercase">
        Uploaded document
      </div>

      {upload ? (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 flex-shrink-0 text-[color:var(--accent-true-blue)]" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-fs-micro text-[color:var(--text-primary)]">
              {upload.filename}
            </p>
            {upload.size !== undefined && (
              <p className="font-mono text-fs-micro text-[color:var(--text-muted)]">
                {humanSize(upload.size)} · shown instead of the sections below
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy || !bffEnabled}
            className="rounded border border-[rgba(var(--accent-true-blue-rgb),0.3)] px-2 py-1 font-mono text-fs-label tracking-widest text-[color:var(--text-secondary)] uppercase transition-colors hover:text-[color:var(--text-primary)] disabled:opacity-40"
          >
            Replace
          </button>
          <button
            type="button"
            aria-label="Remove uploaded document"
            onClick={() => clearPreReading(wsId)}
            className="rounded p-1 text-[color:var(--text-muted)] transition-colors hover:text-[color:var(--accent-red)]"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <p className="font-mono text-fs-label text-[color:var(--text-muted)]">
            {bffEnabled
              ? "Upload a PDF or document to use as the pre-reading."
              : "Document upload needs the document backend (BFF)."}
          </p>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy || !bffEnabled}
            className="flex items-center gap-1.5 rounded border border-dashed border-[rgba(var(--accent-true-blue-rgb),0.35)] px-3 py-1.5 font-mono text-fs-label tracking-[0.15em] text-[color:var(--accent-true-blue)] uppercase transition-colors hover:bg-[rgba(var(--accent-true-blue-rgb),0.08)] disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
            ) : (
              <UploadIcon className="h-3 w-3" aria-hidden />
            )}
            Upload document
          </button>
        </div>
      )}
      <input ref={fileRef} type="file" className="hidden" onChange={(e) => void onFile(e)} />
    </div>
  );
}

/** Pre-reading editor — upload a document (takes precedence) and/or author
 *  the dossier title + prose/callout sections. */
export function PreReadingEditor({ wsId }: { wsId: Workshop["id"] }) {
  const override = useEffectiveWorkshopOverride(wsId);
  const def = DOC_BY_WORKSHOP[wsId];
  const current = resolveField(override, "doc", def);
  const [title, setTitle] = useState(current.title);
  const [sections, setSections] = useState<EditableSection[]>(
    current.sections.map(toEditable),
  );

  const built: DocContent = { title, sections: sections.map(fromEditable) };
  const setSection = (i: number, patch: Partial<EditableSection>) =>
    setSections((prev) => prev.map((s, j) => (j === i ? { ...s, ...patch } : s)));

  return (
    <SectionFrame
      description="Upload a document to use as the pre-reading, or author the dossier sections below."
      overridden={override?.doc !== undefined}
      dirty={!sameJson(built, current)}
      onSave={() => setEffectiveOverride(wsId, { doc: built })}
      onReset={() => {
        clearEffectiveOverrideField(wsId, "doc");
        setTitle(def.title);
        setSections(def.sections.map(toEditable));
      }}
    >
      <UploadBlock wsId={wsId} />

      <Field label="Dossier title">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>

      {sections.map((s, i) => (
        <ListCard
          key={i}
          title={`Section ${i + 1}`}
          onMoveUp={i > 0 ? () => setSections(moveItem(sections, i, -1)) : undefined}
          onMoveDown={
            i < sections.length - 1 ? () => setSections(moveItem(sections, i, 1)) : undefined
          }
          onRemove={() => setSections(sections.filter((_, j) => j !== i))}
        >
          <div className="grid grid-cols-[1fr_auto] gap-2">
            <Field label="Heading">
              <TextInput value={s.heading} onChange={(e) => setSection(i, { heading: e.target.value })} />
            </Field>
            <Field label="Style">
              <Select
                value={s.kind}
                onChange={(v) => setSection(i, { kind: v as EditableSection["kind"] })}
                options={[
                  { value: "section", label: "prose" },
                  { value: "callout", label: "callout" },
                ]}
              />
            </Field>
          </div>
          <Field label="Body" hint="Separate paragraphs with a blank line.">
            <TextArea value={s.bodyText} onChange={(e) => setSection(i, { bodyText: e.target.value })} />
          </Field>
          <Field label="List intro (optional)">
            <TextInput value={s.intro} onChange={(e) => setSection(i, { intro: e.target.value })} />
          </Field>
          <Field label="List items (optional)" hint="One bullet per line.">
            <TextArea value={s.itemsText} onChange={(e) => setSection(i, { itemsText: e.target.value })} />
          </Field>
        </ListCard>
      ))}
      <AddButton
        label="Add section"
        onClick={() =>
          setSections([
            ...sections,
            { heading: "", kind: "section", bodyText: "", intro: "", itemsText: "" },
          ])
        }
      />
    </SectionFrame>
  );
}
